import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Fuel, Gauge, Calendar, Settings2, Heart, BadgeCheck, Share2, Users, CalendarCheck, MapPin, Copy, Mail, Twitter, Facebook, Linkedin } from 'lucide-react';
import { useFavorites } from '@/context/FavoritesContext';
import { useSiteSettings } from '@/context/SiteSettingsContext';
import { useLanguage } from '@/context/LanguageContext';
import { useTheme } from '@/context/ThemeContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const CarCard = ({ car, index = 0, showFavorite = true }) => {
  const { isFavorite, toggleFavorite } = useFavorites();
  const { settings } = useSiteSettings();
  const { language } = useLanguage();
  const { theme } = useTheme();
  const isCarFavorite = isFavorite(car.id);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const shareMenuRef = useRef(null);

  // Close share menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (shareMenuRef.current && !shareMenuRef.current.contains(e.target)) {
        setShowShareMenu(false);
      }
    };
    if (showShareMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showShareMenu]);

  // Get sold banner colors from settings
  const soldBannerGradient = settings ? 
    `linear-gradient(to right, ${settings.sold_banner_color_from || '#059669'}, ${settings.sold_banner_color_via || '#22c55e'}, ${settings.sold_banner_color_to || '#059669'})` :
    'linear-gradient(to right, #059669, #22c55e, #059669)';
  
  const soldBannerText = settings ? 
    (language === 'en' ? (settings.sold_banner_text_en || 'Sold') : (settings.sold_banner_text || 'Verkauft')) :
    'Verkauft';

  const formatPrice = (price) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0
    }).format(price);
  };

  // Handle image URL - check if it's a relative path or absolute URL
  const getImageUrl = (url) => {
    if (!url) return 'https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=800';
    if (url.startsWith('http')) return url;
    return `${API_URL}${url}`;
  };

  const handleFavoriteClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const success = await toggleFavorite(car.id);
    if (success) {
      toast.success(isCarFavorite ? 'Removed from wishlist' : 'Added to wishlist');
    }
  };

  const handleShare = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const shareUrl = `${window.location.origin}/cars/${car.id}`;
    const shareTitle = `${car.brand} ${car.model} - ${formatPrice(car.price)}`;
    const shareText = `${car.brand} ${car.model} (${car.year}) - ${formatPrice(car.price)}\n${car.horsepower} PS • ${car.fuel_type} • ${car.transmission}`;
    
    // Get the image URL
    const imageUrl = getImageUrl(car.images?.[0]);
    
    // Check if mobile (touch device or narrow screen)
    const isMobile = 'ontouchstart' in window || navigator.maxTouchPoints > 0 || window.innerWidth < 768;
    
    // Try native share API on mobile
    if (isMobile && navigator.share) {
      try {
        let shareData = {
          title: shareTitle,
          text: shareText,
          url: shareUrl
        };
        
        // Try to include image if possible
        if (navigator.canShare && imageUrl) {
          try {
            const response = await fetch(imageUrl);
            const blob = await response.blob();
            const file = new File([blob], `${car.brand}-${car.model}.jpg`, { type: 'image/jpeg' });
            
            const dataWithFile = { ...shareData, files: [file] };
            if (navigator.canShare(dataWithFile)) {
              shareData = dataWithFile;
            }
          } catch (imgError) {
            console.log('Could not fetch image for sharing:', imgError);
          }
        }
        
        await navigator.share(shareData);
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Share failed:', err);
        }
      }
    } else {
      // Desktop: Show share menu dropdown
      setShowShareMenu(!showShareMenu);
    }
  };

  const handleShareOption = async (platform, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const shareUrl = encodeURIComponent(`${window.location.origin}/cars/${car.id}`);
    const shareText = encodeURIComponent(`${car.brand} ${car.model} (${car.year}) - ${formatPrice(car.price)}`);
    
    let url = '';
    switch (platform) {
      case 'copy':
        try {
          await navigator.clipboard.writeText(`${car.brand} ${car.model} - ${formatPrice(car.price)}\n${window.location.origin}/cars/${car.id}`);
          toast.success(language === 'en' ? 'Link copied!' : 'Link kopiert!');
        } catch (err) {
          console.error('Copy failed:', err);
        }
        break;
      case 'email':
        url = `mailto:?subject=${shareText}&body=${encodeURIComponent(`${car.brand} ${car.model}\n${window.location.origin}/cars/${car.id}`)}`;
        window.open(url, '_self');
        break;
      case 'twitter':
        url = `https://twitter.com/intent/tweet?text=${shareText}&url=${shareUrl}`;
        window.open(url, '_blank', 'width=550,height=450');
        break;
      case 'facebook':
        url = `https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`;
        window.open(url, '_blank', 'width=550,height=450');
        break;
      case 'linkedin':
        url = `https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`;
        window.open(url, '_blank', 'width=550,height=450');
        break;
      case 'whatsapp':
        url = `https://wa.me/?text=${shareText}%20${shareUrl}`;
        window.open(url, '_blank');
        break;
      default:
        break;
    }
    setShowShareMenu(false);
  };

  return (
    <div className="relative">
      <Link 
        to={`/cars/${car.id}`}
        className="card-premium group block opacity-0 animate-fade-in"
        style={{ animationDelay: `${index * 0.1}s` }}
        data-testid={`car-card-${car.id}`}
      >
        {/* Image Container */}
        <div className="car-image-container aspect-[4/3] bg-muted relative">
          <img 
            src={getImageUrl(car.images?.[0])}
            alt={`${car.brand} ${car.model}`}
            className={`w-full h-full object-cover ${car.is_sold ? 'grayscale-[30%]' : ''}`}
            loading="lazy"
          />
          
          {/* Location Badge */}
          {car.location && (
            <div 
              className={`absolute bottom-3 left-3 px-3 py-1.5 rounded-sm flex items-center gap-1.5 text-xs font-medium shadow-md backdrop-blur-sm ${
                theme === 'dark' 
                  ? 'bg-black/80 text-white' 
                  : 'bg-white/90 text-gray-800'
              }`}
              data-testid="car-location-badge"
            >
              <MapPin size={12} strokeWidth={2} />
              <span>{car.location}</span>
            </div>
          )}
          
          {/* SOLD Banner */}
          {car.is_sold && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="absolute inset-0 bg-black/20" />
              <div className="relative transform -rotate-12">
                <div 
                  className="px-8 py-2 shadow-xl"
                  style={{ background: soldBannerGradient }}
                >
                  <div className="flex items-center gap-2">
                    <BadgeCheck size={20} className="text-white" />
                    <span className="text-white font-bold text-lg tracking-widest uppercase">
                      {soldBannerText}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Brand & Model */}
          <div className="flex justify-between items-start mb-4">
            <div>
              <p className="overline mb-1">{car.brand}</p>
              <h3 className="font-heading text-xl md:text-2xl font-light">{car.model}</h3>
            </div>
            <div className="text-right">
              <span className={`font-heading text-xl md:text-2xl font-light ${car.is_sold ? 'text-emerald-600' : 'text-gold'}`}>
                {formatPrice(car.price)}
              </span>
              {car.is_sold && (
                <p className="text-xs text-emerald-600 font-medium mt-1">Verkauft</p>
              )}
            </div>
          </div>

          {/* Specs */}
          <div className="grid grid-cols-2 gap-3 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <Calendar size={14} strokeWidth={1.5} />
              <span>{car.year}</span>
            </div>
            <div className="flex items-center gap-2">
              <Gauge size={14} strokeWidth={1.5} />
              <span>{car.horsepower} PS</span>
            </div>
            {car.mileage && (
              <div className="flex items-center gap-2">
                <Settings2 size={14} strokeWidth={1.5} />
                <span>{car.mileage.toLocaleString('de-DE')} km</span>
              </div>
            )}
            {(car.first_registration_month || car.first_registration_year) && (
              <div className="flex items-center gap-2">
                <CalendarCheck size={14} strokeWidth={1.5} />
                <span>EZ {car.first_registration_month ? `${car.first_registration_month}/` : ''}{car.first_registration_year || ''}</span>
              </div>
            )}
            {car.previous_owners !== null && car.previous_owners !== undefined && (
              <div className="flex items-center gap-2">
                <Users size={14} strokeWidth={1.5} />
                <span>{car.previous_owners} {car.previous_owners === 1 ? 'Vorbesitzer' : 'Vorbesitzer'}</span>
              </div>
            )}
            <div className="flex items-center gap-2">
              <Fuel size={14} strokeWidth={1.5} />
              <span>{car.fuel_type}</span>
            </div>
          </div>
        </div>
      </Link>

      {/* Action Buttons */}
      {showFavorite && !car.is_sold && (
        <div className="absolute top-4 right-4 flex gap-2 z-10">
          {/* Share Button with Dropdown */}
          <div className="relative" ref={shareMenuRef}>
            <button
              onClick={handleShare}
              className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${showShareMenu ? 'bg-blue-500 text-white' : 'bg-card/80 backdrop-blur-sm text-muted-foreground hover:bg-blue-500 hover:text-white'}`}
              data-testid={`share-btn-${car.id}`}
              aria-label="Share"
            >
              <Share2 size={18} />
            </button>
            
            {/* Share Menu Dropdown (Desktop) */}
            {showShareMenu && (
              <div className="absolute top-12 right-0 bg-card border border-border rounded-lg shadow-xl py-2 min-w-[180px] z-50">
                <button
                  onClick={(e) => handleShareOption('copy', e)}
                  className="w-full px-4 py-2 flex items-center gap-3 text-sm hover:bg-muted transition-colors text-left"
                >
                  <Copy size={16} />
                  <span>{language === 'en' ? 'Copy Link' : 'Link kopieren'}</span>
                </button>
                <button
                  onClick={(e) => handleShareOption('email', e)}
                  className="w-full px-4 py-2 flex items-center gap-3 text-sm hover:bg-muted transition-colors text-left"
                >
                  <Mail size={16} />
                  <span>E-Mail</span>
                </button>
                <button
                  onClick={(e) => handleShareOption('whatsapp', e)}
                  className="w-full px-4 py-2 flex items-center gap-3 text-sm hover:bg-muted transition-colors text-left"
                >
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                  <span>WhatsApp</span>
                </button>
                <div className="border-t border-border my-1"></div>
                <button
                  onClick={(e) => handleShareOption('facebook', e)}
                  className="w-full px-4 py-2 flex items-center gap-3 text-sm hover:bg-muted transition-colors text-left"
                >
                  <Facebook size={16} />
                  <span>Facebook</span>
                </button>
                <button
                  onClick={(e) => handleShareOption('twitter', e)}
                  className="w-full px-4 py-2 flex items-center gap-3 text-sm hover:bg-muted transition-colors text-left"
                >
                  <Twitter size={16} />
                  <span>Twitter / X</span>
                </button>
                <button
                  onClick={(e) => handleShareOption('linkedin', e)}
                  className="w-full px-4 py-2 flex items-center gap-3 text-sm hover:bg-muted transition-colors text-left"
                >
                  <Linkedin size={16} />
                  <span>LinkedIn</span>
                </button>
              </div>
            )}
          </div>
          
          {/* Favorite Button */}
          <button
            onClick={handleFavoriteClick}
            className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
              isCarFavorite 
                ? 'bg-gold text-white' 
                : 'bg-card/80 backdrop-blur-sm text-muted-foreground hover:bg-gold hover:text-white'
            }`}
            data-testid={`favorite-btn-${car.id}`}
            aria-label={isCarFavorite ? 'Remove from wishlist' : 'Add to wishlist'}
          >
            <Heart size={18} fill={isCarFavorite ? 'currentColor' : 'none'} />
          </button>
        </div>
      )}
      
      {/* Share Button for sold cars */}
      {car.is_sold && (
        <div className="absolute top-4 right-4 z-10" ref={!showFavorite || car.is_sold ? shareMenuRef : null}>
          <button
            onClick={handleShare}
            className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${showShareMenu ? 'bg-blue-500 text-white' : 'bg-card/80 backdrop-blur-sm text-muted-foreground hover:bg-blue-500 hover:text-white'}`}
            data-testid={`share-btn-${car.id}`}
            aria-label="Share"
          >
            <Share2 size={18} />
          </button>
          
          {/* Share Menu Dropdown for sold cars (Desktop) */}
          {showShareMenu && (
            <div className="absolute top-12 right-0 bg-card border border-border rounded-lg shadow-xl py-2 min-w-[180px] z-50">
              <button
                onClick={(e) => handleShareOption('copy', e)}
                className="w-full px-4 py-2 flex items-center gap-3 text-sm hover:bg-muted transition-colors text-left"
              >
                <Copy size={16} />
                <span>{language === 'en' ? 'Copy Link' : 'Link kopieren'}</span>
              </button>
              <button
                onClick={(e) => handleShareOption('email', e)}
                className="w-full px-4 py-2 flex items-center gap-3 text-sm hover:bg-muted transition-colors text-left"
              >
                <Mail size={16} />
                <span>E-Mail</span>
              </button>
              <button
                onClick={(e) => handleShareOption('whatsapp', e)}
                className="w-full px-4 py-2 flex items-center gap-3 text-sm hover:bg-muted transition-colors text-left"
              >
                <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                <span>WhatsApp</span>
              </button>
              <div className="border-t border-border my-1"></div>
              <button
                onClick={(e) => handleShareOption('facebook', e)}
                className="w-full px-4 py-2 flex items-center gap-3 text-sm hover:bg-muted transition-colors text-left"
              >
                <Facebook size={16} />
                <span>Facebook</span>
              </button>
              <button
                onClick={(e) => handleShareOption('twitter', e)}
                className="w-full px-4 py-2 flex items-center gap-3 text-sm hover:bg-muted transition-colors text-left"
              >
                <Twitter size={16} />
                <span>Twitter / X</span>
              </button>
              <button
                onClick={(e) => handleShareOption('linkedin', e)}
                className="w-full px-4 py-2 flex items-center gap-3 text-sm hover:bg-muted transition-colors text-left"
              >
                <Linkedin size={16} />
                <span>LinkedIn</span>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CarCard;
